import { useQuery } from "@tanstack/react-query";
import { get } from "@/shared/utils/fetchApi";

/**
 * Interface para a resposta da API de versão
 */
interface VersionResponse {
  version: string;
  buildNumber?: string;
  buildDate?: string;
  environment?: string;
}

interface UseVersionOptions {
  enabled?: boolean;
}

/**
 * Hook para buscar informações de versão do backend
 * @param options - Opções de configuração
 * @param options.enabled - Se false, não executa a query até que seja true
 */
export const useVersion = (options?: UseVersionOptions) => {
  return useQuery<VersionResponse>({
    queryKey: ["backend-version"],
    queryFn: async () => {
      const response = await get<Record<string, unknown>>("/version");

      // Se a resposta já tem o formato esperado, retorna diretamente
      if (response && typeof response === "object" && "version" in response) {
        return response as unknown as VersionResponse;
      }

      // Se a resposta está dentro de uma propriedade data, retorna response.data
      if (
        response &&
        typeof response === "object" &&
        response["data"] &&
        typeof response["data"] === "object" &&
        "version" in (response["data"] as object)
      ) {
        return response["data"] as VersionResponse;
      }

      // Se não conseguiu obter versão válida, lança erro para React Query tratar
      throw new Error("Invalid version response format");
    },
    enabled: options?.enabled !== false, // Por padrão está habilitado, mas pode ser desabilitado
    staleTime: 1000 * 60 * 60, // 1 hora de cache
    retry: false, // Não tenta novamente em caso de erro
  });
};
